import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { CONFIG_BASE_DATE_RANGEPICKER } from '../../../../../../../core/constants/constants';
import { RepeatedChargesWithdrawalsVoucherByDateRange } from '../../../../../../../core/interfaces/charges-withdrawals-request.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { ChargesWithdrawalsRequestService } from '../../../../../../../core/services/charges-withdrawals-request.service';
import { extractErrorMessage } from '../../../../../../../core/utils/extract-error-message.util';
import { DynamicModalService } from '../../../../../../../core/services/dynamic-modal.service';
import { FilterCommunicationService } from '../../../../../../../core/services/filter-communication.service';
import { selectAuth } from '../../../../../../../core/state/auth/auth.selector';
import { REQUEST_WITHDRAWAL_PERMISSIONS } from '../../../../../../../shared/data/config/constants-buttons';
import { MAIN_PERMISSIONS, ROLES } from '../../../../../../../shared/data/config/constants';
import { RepeatedCwVouchersProfile } from '../../../../../../../core/interfaces/ui/repeated-cw-vouchers.interfaces';

@Component({
  selector: 'app-repeated-cw-vouchers-modal',
  templateUrl: './repeated-cw-vouchers-modal.component.html',
  styleUrl: './repeated-cw-vouchers-modal.component.scss',
})
export class RepeatedCwVouchersModalComponent implements OnDestroy {
  public modalHost?: {
    minimize: () => void;
    restore: () => void;
  };
  public readonly bsConfig = CONFIG_BASE_DATE_RANGEPICKER.bsConfig;
  public readonly initialRange: Date[] = CONFIG_BASE_DATE_RANGEPICKER.initialRange;

  public isLoading = false;
  public hasSearched = false;
  public errorMessage = '';
  public rows: RepeatedChargesWithdrawalsVoucherByDateRange[] = [];
  public page = 1;
  public pageSize = 10;
  private readonly destroy$ = new Subject<void>();
  private profile: RepeatedCwVouchersProfile | null = null;

  public form = this._fb.group({
    dateRange: [this.initialRange, Validators.required],
  });

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _cwService: ChargesWithdrawalsRequestService,
    private readonly _filterCommunicationService: FilterCommunicationService,
    private readonly _dynamicModalService: DynamicModalService,
    private readonly store: Store,
  ) {
    this.store
      .select(selectAuth)
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: RepeatedCwVouchersProfile) => {
        this.profile = profile;
      });
  }

  get totalResults(): number {
    return this.rows.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalResults / this.pageSize));
  }

  get totalRiskAmount(): number {
    return this.rows
      .filter((item) => Number(item.monto) > 0)
      .reduce((acc, item) => acc + Number(item.monto), 0);
  }

  get paginatedRows(): RepeatedChargesWithdrawalsVoucherByDateRange[] {
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.rows.slice(start, end);
  }

  search(): void {
    const dateRange = this.form.get('dateRange')?.value as Date[] | null;

    if (!dateRange || dateRange.length < 2 || !dateRange[0] || !dateRange[1]) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.hasSearched = true;
    this.errorMessage = '';

    this._cwService
      .getRepeatedVouchersByDateRange({
        startDate: moment(dateRange[0]).startOf('day').toDate(),
        endDate: moment(dateRange[1]).endOf('day').toDate(),
      })
      .subscribe({
        next: (response) => {
          this.rows = this.filterRowsByFinancialPermission(response.data);
          this.page = 1;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = extractErrorMessage(
            error,
            'es',
            'No se pudo obtener la información de comprobantes repetidos.',
          );
          this.rows = [];
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageChange(page: number): void {
    this.page = Math.min(Math.max(page, 1), this.totalPages);
  }

  goToFirstPage(): void {
    this.onPageChange(1);
  }

  goToPreviousPage(): void {
    this.onPageChange(this.page - 1);
  }

  goToNextPage(): void {
    this.onPageChange(this.page + 1);
  }

  goToLastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onPageInputChange(value: string | number): void {
    const nextPage = Number(value);

    if (!Number.isFinite(nextPage)) {
      this.page = 1;
      return;
    }

    this.onPageChange(nextPage);
  }

  getSeverityClass(
    row: RepeatedChargesWithdrawalsVoucherByDateRange,
  ): 'high' | 'med' {
    return Number(row.total) >= 3 ? 'high' : 'med';
  }

  getSeverityLabel(row: RepeatedChargesWithdrawalsVoucherByDateRange): string {
    return Number(row.total) >= 3 ? 'Alta' : 'Media';
  }

  private filterRowsByFinancialPermission(
    rows: RepeatedChargesWithdrawalsVoucherByDateRange[],
  ): RepeatedChargesWithdrawalsVoucherByDateRange[] {
    if (this.isRestrictedRole()) {
      return rows.filter((row) => !this.isFinancialRow(row));
    }

    if (this.hasFinancialDataPermission()) {
      return rows;
    }

    return rows.filter((row) => !this.isFinancialRow(row));
  }

  private isRestrictedRole(): boolean {
    return (
      this.profile?.rol?.id === ROLES.PERSONAL_SERVICE ||
      this.profile?.rol?.id === ROLES.SERVICE_BOSS
    );
  }

  private hasFinancialDataPermission(): boolean {
    const mainPermission = this.profile?.rol?.permisos_principales;

    if (!Array.isArray(mainPermission)) {
      return false;
    }

    const subscriptionsPayments = mainPermission.find(
      (permission: { id: number }) =>
        permission.id === MAIN_PERMISSIONS['Abonos y Pagos'],
    );

    if (!subscriptionsPayments) {
      return false;
    }

    return subscriptionsPayments.permisos.some(
      (permission: { id: number; activar: number }) =>
        permission.id === REQUEST_WITHDRAWAL_PERMISSIONS.GET_FINANCIAL_DATA &&
        permission.activar === 1,
    );
  }

  private isFinancialRow(
    row: RepeatedChargesWithdrawalsVoucherByDateRange,
  ): boolean {
    return Number(row.has_financial) === 1;
  }

  exportCsv(): void {
    const headers = ['Comprobante', 'Monto total', 'Severidad', 'Repetidos'];
    const lines = this.rows.map((row) =>
      [
        `"${row.n_comprobante || 'Sin comprobante'}"`,
        Number(row.monto).toFixed(2),
        this.getSeverityLabel(row),
        `x${row.total}`,
      ].join(',')
    );

    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'comprobantes-repetidos-cw.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  applyRowFilter(row: RepeatedChargesWithdrawalsVoucherByDateRange): void {
    const modalData = this._dynamicModalService.getModalData();
    const currentFilter = modalData?.currentFilter || {};
    const dateRange = this.form.get('dateRange')?.value as Date[] | null;

    const dateStart =
      dateRange && dateRange[0]
        ? moment(dateRange[0]).format('YYYY-MM-DD 00:00:00')
        : null;
    const dateEnd =
      dateRange && dateRange[1]
        ? moment(dateRange[1]).format('YYYY-MM-DD 23:59:59')
        : null;

    this._filterCommunicationService.changeFilter({
      ...currentFilter,
      receipt: row.n_comprobante?.trim() || '',
      // The group is keyed by voucher number only, so amounts inside it can
      // differ. Filtering by one amount would hide the other repetitions.
      amount: null,
      dateRange,
      date_star: dateStart,
      date_end: dateEnd,
      dateUpdate: null,
      date_star_update: null,
      date_end_update: null,
    });

    this.modalHost?.minimize();
  }
}
